// docs/71: which hero's inventory is open and on which tab, and the drag in flight. Kept apart
// so any screen can open the inventory (the World's party chips, the I key) without importing it.
import { create } from 'zustand';

export type InventoryTab = 'gear' | 'trades';

interface InventoryStore {
  heroId?: bigint;
  tab: InventoryTab;
  /** The item whose details are showing. */
  selected?: bigint;
  open(heroId: bigint, opts?: { tab?: InventoryTab; select?: bigint }): void;
  close(): void;
  setTab(tab: InventoryTab): void;
  select(id: bigint | undefined): void;
}

export const useInventory = create<InventoryStore>()((set) => ({
  heroId: undefined,
  tab: 'gear',
  open: (heroId, opts) => set({ heroId, tab: opts?.tab ?? 'gear', selected: opts?.select }),
  close: () => set({ heroId: undefined, selected: undefined }),
  setTab: (tab) => set({ tab }),
  select: (selected) => set({ selected }),
}));

export const openInventory = (heroId: bigint, opts?: { tab?: InventoryTab; select?: bigint }) => useInventory.getState().open(heroId, opts);

/** Where a dragged item may be dropped: a worn slot, the pack, the discard pile, or another of my heroes. */
export type DropTarget = { kind: 'slot'; slot: number } | { kind: 'pack' } | { kind: 'trash' } | { kind: 'hero'; heroId: bigint };

export function parseDrop(s: string | undefined | null): DropTarget | undefined {
  if (!s) return undefined;
  const [k, v] = s.split(':');
  if (k === 'slot') return { kind: 'slot', slot: Number(v) };
  if (k === 'pack') return { kind: 'pack' };
  if (k === 'trash') return { kind: 'trash' };
  if (k === 'hero' && v) return { kind: 'hero', heroId: BigInt(v) };
  return undefined;
}
export const dropKey = (t: DropTarget | undefined): string => (!t ? '' : t.kind === 'slot' ? `slot:${t.slot}` : t.kind === 'hero' ? `hero:${t.heroId}` : t.kind);

interface DragStore {
  /** The item being dragged, its def and name (what its icon is drawn from), and the pointer. */
  item?: bigint;
  def?: number;
  name?: string;
  x: number;
  y: number;
  over?: DropTarget;
  start(item: bigint, def: number, name: string, x: number, y: number): void;
  move(x: number, y: number, over: DropTarget | undefined): void;
  end(): void;
}

export const useDrag = create<DragStore>()((set) => ({
  item: undefined, def: undefined, name: undefined, x: 0, y: 0, over: undefined,
  start: (item, def, name, x, y) => set({ item, def, name, x, y, over: undefined }),
  move: (x, y, over) => set({ x, y, over }),
  end: () => set({ item: undefined, def: undefined, name: undefined, over: undefined }),
}));

/** The drop target under a point: the nearest `[data-drop]` ancestor of what is there. */
export function dropAt(x: number, y: number): DropTarget | undefined {
  const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-drop]');
  return parseDrop(el?.dataset.drop);
}
